import { useMemo } from 'react';
import { Wand2 } from 'lucide-react';
import { HECHIZOS, obtenerHechizo } from '@/engine/sorcery';
import { obtenerSkill } from '@/engine/skills';
import { useCharacterStore } from '@/engine/state';
import { FullscreenPage } from '@/components/ui';
import type { Spell } from '@/types';
import { cn } from '@/utils/cn';

export function SpellBookView() {
  const aprendidos = useCharacterStore((s) => s.personaje.hechizosAprendidos);
  const manaActual = useCharacterStore((s) => s.personaje.stats.mana);

  const { conocidos, porDescubrir } = useMemo(() => {
    const c = aprendidos
      .map((id) => obtenerHechizo(id))
      .filter((s): s is Spell => s !== undefined);
    const pd = HECHIZOS.filter((h) => !aprendidos.includes(h.id));
    return { conocidos: c, porDescubrir: pd };
  }, [aprendidos]);

  return (
    <FullscreenPage
      titulo="Libro de hechizos"
      subtitulo={`${conocidos.length} de ${HECHIZOS.length} conocidos`}
    >
      <div className="space-y-5">
        <section>
          <h2 className="font-display fluid-base text-moss-300 uppercase tracking-widest mb-2">
            Conocidos
          </h2>
          {conocidos.length === 0 ? (
            <p className="italic text-ash-500 text-narrative fluid-sm">
              Todavía no aprendiste ningún hechizo.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {conocidos.map((s) => (
                <SpellCard key={s.id} spell={s} conocido puedeCastear={manaActual >= s.costoMana} />
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-display fluid-base text-ash-500 uppercase tracking-widest mb-2">
            Por descubrir
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {porDescubrir.map((s) => (
              <SpellCard key={s.id} spell={s} conocido={false} puedeCastear={false} />
            ))}
          </div>
        </section>
      </div>
    </FullscreenPage>
  );
}

function SpellCard({
  spell,
  conocido,
  puedeCastear,
}: {
  spell: Spell;
  conocido: boolean;
  puedeCastear: boolean;
}) {
  const skillRequerida = spell.requiereSkill ? obtenerSkill(spell.requiereSkill) : undefined;
  return (
    <article
      className={cn(
        'rounded-md border p-3 bg-ash-950/60 backdrop-blur-sm',
        conocido ? 'border-moss-800/60' : 'border-ash-800 opacity-70',
      )}
    >
      <header className="flex items-start gap-2 mb-1">
        <Wand2
          className={cn(
            'w-3.5 h-3.5 mt-1 shrink-0',
            conocido ? (puedeCastear ? 'text-moss-300' : 'text-ash-500') : 'text-ash-600',
          )}
        />
        <div className="flex-1 min-w-0">
          <h3
            className={cn(
              'font-display fluid-sm',
              conocido ? 'text-ash-100' : 'text-ash-500',
            )}
          >
            {conocido ? spell.nombre : '???'}
          </h3>
          <p className="fluid-xs uppercase tracking-widest font-ui text-ash-500 mt-0.5">
            {spell.costoMana} MP · {spell.contexto.join(' / ')}
          </p>
        </div>
      </header>
      <p className={cn('fluid-xs clamp-3', conocido ? 'text-ash-300' : 'text-ash-600 italic')}>
        {conocido
          ? spell.descripcion
          : skillRequerida
            ? (
                <>
                  Requiere la habilidad{' '}
                  <span className="text-moss-400 not-italic">«{skillRequerida.nombre}»</span>.
                </>
              )
            : 'Se desbloquea en un evento narrativo.'}
      </p>
    </article>
  );
}
